import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { createLogger } from '@/lib/logger';

const log = createLogger('settingsStore');

// ============================================================================
// settingsStore — ЕДИНОЕ хранилище настроек (как 1С «Хранилище настроек»):
// key-value (JSON) с привязкой к пользователю. SSoT — сервер (user_settings).
// Чтение: свои + глобальные (свои переопределяют глобальные) — RPC
// GetUserSettings. Запись: кэш + дебаунс 1с → SetUserSetting (своя настройка).
// Страница «Настройки» в конфигураторе — ListAllUserSettings (админ).
//
// БАЗЫ-КАК-СХЕМЫ: настройки живут В СХЕМЕ БАЗЫ. Без активной базы запрос
// невалиден (сервер: «no base selected») — гидрация ждёт выбора базы;
// СМЕНА базы → автоматическая перегидрация (hydratedSlug).
// ============================================================================

type Listener = () => void;

const cache = new Map<string, string>();
const listeners = new Set<Listener>();
let hydrated = false;
let hydratedSlug: string | null = null;
// Единый запрос на базу: параллельные вызовы hydrate ждут ОДИН результат
// (иначе второй вызов возвращался до загрузки и читал пустой кэш).
let hydrating: { slug: string; promise: Promise<void> } | null = null;
let dirty = new Map<string, string>();
let removedKeys = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function emit() {
  for (const l of listeners) l();
}

async function flush() {
  if (dirty.size === 0 && removedKeys.size === 0) return;
  const removals = [...removedKeys];
  const batch = [...dirty.entries()];
  removedKeys = new Set();
  dirty = new Map();
  // СВОЯ настройка: явный id текущего пользователя (пустой user_id = глобальная)
  const uid = useAuthStore.getState().user?.id || '';
  // Удаления — первыми: последовательность «запись → удаление» должна
  // примениться на сервере в исходном порядке.
  for (const key of removals) {
    try {
      await getSDK().metadata.deleteUserSetting({ key, userId: uid });
    } catch (e) {
      // Офлайн/ошибка — возврат в очередь (попытка при следующем flush)
      removedKeys.add(key);
      log.error('settings delete failed', { key, error: e instanceof Error ? e.message : String(e) });
    }
  }
  for (const [key, value] of batch) {
    try {
      await getSDK().metadata.setUserSetting({ key, value, userId: uid });
    } catch (e: any) {
      // Офлайн/ошибка — вернуть в очередь (попытка при следующем flush)
      dirty.set(key, value);
      log.error('settings flush failed', { key, error: e?.message || String(e) });
    }
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, 1000);
}

/**
 * Досылка отложенных записей/удалений настроек (офлайн-очередь в памяти).
 * Вызывается при ПОДТВЕРЖДЁННОМ восстановлении связи (connection store,
 * событийная модель — без таймеров): черновики и прочие настройки,
 * не ушедшие на сервер офлайн, не ждут следующей записи.
 */
export function flushUserSettings(): void {
  void flush();
}

async function hydrateSettings(slug: string): Promise<void> {
  try {
    const r: any = await getSDK().metadata.getUserSettings({});
    cache.clear();
    // Глобальные (user_id пуст) + свои — свои переопределяют по ключу
    for (const s of (r?.items || []) as any[]) {
      cache.set(String(s.key || ''), String(s.value ?? ''));
    }
    hydrated = true;
    hydratedSlug = slug;
    emit();
  } catch (e: any) {
    log.error('settings hydrate failed', { error: e?.message || String(e) });
  }
}

export const settingsStore = {
  hydrate(): Promise<void> {
    // ГЕЙТ: без активной базы настроек не существует — не ходим на сервер
    // (лаунчер); смена базы сбрасывает кэш и перегидрирует.
    const slug = useCenterStore.getState().baseSlug;
    if (!slug) {
      if (hydrated) {
        hydrated = false;
        hydratedSlug = null;
        cache.clear();
      }
      return Promise.resolve();
    }
    if (hydrated && hydratedSlug === slug) return Promise.resolve();
    // Один запрос на базу: до гидрации настройки читают сразу НЕСКОЛЬКО мест
    // (черновики чатов — по строке списка), дублирующие запросы не нужны.
    if (hydrating && hydrating.slug === slug) return hydrating.promise;
    const promise = hydrateSettings(slug);
    hydrating = { slug, promise };
    void promise.finally(() => {
      if (hydrating && hydrating.promise === promise) hydrating = null;
    });
    return promise;
  },

  /** Настройки загружены с сервера (без гидрации запись/чтение черновиков неполноценны). */
  isHydrated(): boolean {
    return hydrated;
  },

  get(key: string): string | undefined {
    if (!hydrated) void this.hydrate();
    return cache.get(key);
  },

  getJSON<T>(key: string, def: T): T {
    const v = this.get(key);
    if (v === undefined || v === '') return def;
    try {
      return JSON.parse(v) as T;
    } catch {
      return def;
    }
  },

  set(key: string, value: string): void {
    cache.set(key, value);
    removedKeys.delete(key);
    dirty.set(key, value);
    emit();
    scheduleFlush();
  },

  /**
   * Удаление настройки: кэш чистится сразу, удаление на сервере — в общем
   * flush (офлайн — повторится при следующей попытке). Удаление — ЯВНОЕ
   * намерение (пустое значение ≠ удаление: ключ не воскресает при гидрации).
   */
  remove(key: string): void {
    cache.delete(key);
    dirty.delete(key);
    removedKeys.add(key);
    emit();
    scheduleFlush();
  },

  setJSON(key: string, value: unknown): void {
    this.set(key, JSON.stringify(value));
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },
};
